import { SortDirection } from 'constants/SortDirection';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { EntityName, getPage } from 'stores/_services/dataService';
import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import UserActivityStore from 'stores/userActivity/UserActivityStore';
import { IDataFetchOptions, IGetDataParams } from '../../types';

interface IGetActivitiesOptions extends IDataFetchOptions {
    userId: string;
}

export default class UserActivityListStore extends BaseListStore<UserActivityStore> {
    userActivityData: Record<string, { count: number; ids: string[] }> = {};

    constructor() {
        super({
            sortBy: 'createdOn',
            sortDirection: SortDirection.DESC,
            itemStore: UserActivityStore
        });

        makeObservable<UserActivityListStore, '_updateUserIds'>(this, {
            userActivityData: observable,
            getActivities: action,
            getUserActivities: action,
            _updateUserIds: action
        });
    }

    private _updateUserIds(userId: string, data: any, skip: number) {
        const arr = this.userActivityData[userId].ids;
        arr.length = Math.max(arr.length, data.length + skip);

        for (let i = 0; i < data.length; i++) {
            if (data[i]) {
                // @ts-ignore: We can assume id exists here
                arr[i + skip] = data[i].id;
            }
        }
    }

    findActivitiesForUser(userId: string): UserActivityStore[] {
        return this.userActivityData[userId]?.ids.map((id) => this.data.get(id)) || [];
    }

    async getActivities(args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                order: this.sort,
                include: {
                    relation: 'userIdentity',
                    scope: {
                        fields: ['id', 'username', 'email']
                    }
                }
            }
        };

        const { data, count } = await getPage(`${EntityName.USER_ACTIVITIES}/activity`, options);

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateCount(data);
        this._updateIds(data, this.skip);

        runInAction(() => {
            this.count = data.length;
            this.totalCount = count;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getActivitiesForCorrectPagination(args: IGetDataParams = {}): Promise<void> {
        const { sortBy, sortDirection, pageSize, page } = args;

        const order = `${sortBy} ${sortDirection}`;
        const limit = pageSize || DEFAULT_LIMIT;
        const skip = ((page || DEFAULT_PAGE_NUMBER) - 1) * limit;

        const options = {
            filter: {
                order,
                limit,
                skip,
                include: {
                    relation: 'userIdentity',
                    scope: {
                        fields: ['id', 'username', 'email']
                    }
                }
            }
        };

        const { data, count } = await getPage(`${EntityName.USER_ACTIVITIES}/activity`, options);

        this.updateStateForCorrectPagination<UserActivityStore>({ args, data, totalCount: count });
    }

    async getUserActivities({ userId, ...args }: IGetActivitiesOptions): Promise<void> {
        const shouldReset = this._updateListState(args);

        const options = {
            filter: {
                limit: this.limit,
                skip: this.skip,
                order: this.sort,
                where: { userId },
                include: {
                    relation: 'userIdentity',
                    scope: {
                        fields: ['id', 'username', 'email']
                    }
                }
            }
        };

        const { data } = await getPage(`${EntityName.USER_ACTIVITIES}/activity`, options);

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateCount(data);

        if (!this.userActivityData[userId]) {
            //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
            runInAction(() => {
                this.userActivityData[userId] = { count: 0, ids: [] };
            });
        }

        this._updateUserIds(userId, data, this.skip);

        runInAction(() => {
            this.userActivityData[userId].count = this.count;
        });
    }
}
